import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import HelpButton from 'help/HelpButton';
import {SectionWrapper} from 'metadata/MetadataEditorInfoSection';
import {ContainerViewQuery} from 'samples/containers/__generated/ContainerViewQuery';
import {
  EXPIRATION_DATETIME_FORMATS,
  EXPIRATION_DATETIME_PRESENTATIONAL_FORMAT,
  validateAndConvertExpirationDatetime,
} from 'samples/valid-contents/ValidContentsConstants';
import EditableDateField from 'ui-library/EditableDateField';
import {Title} from 'ui-library/Headers';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {PopoverIconProps} from 'ui-library/PopoverIcon';

export type ExpirationInfo = ExtractByTypename<
  ContainerViewQuery['container'],
  'ContainerNode'
>['expirationInfo'];

type ComputedFromEntity = ExpirationInfo['computedFromEntity'];

interface ContainerExpirationDateProps {
  expirationInfo: ExpirationInfo;
  onUpdateExpirationOverride: (date: string | null) => void;
  isDisabled?: boolean;
  /* If provided, wait for confirmation from this function before entering edit mode */
  confirmBeforeStartEditing?: () => Promise<boolean>;
  /* Disable and render provided tooltip if non-null */
  disableEditsWithTooltip?: string | null;
}

function getInheritanceIconProps({
  expirationIsOverridden,
  computedFromEntity,
  clearExpirationOverride,
  isDisabled,
}: {
  expirationIsOverridden: boolean;
  computedFromEntity: ComputedFromEntity;
  clearExpirationOverride: () => void;
  isDisabled?: boolean;
}): PopoverIconProps | undefined {
  const iconClassNames = `${css(styles.inheritanceIcon)} u-mini-spacing-left`;
  // If there's no computedFromEntity (no expiration to inherit, though expiration tracking is enabled)
  // then there's nothing to render here
  if (computedFromEntity == null) {
    return undefined;
  }
  return !expirationIsOverridden
    ? {
        renderContent: () =>
          `Expiration date inherited from entity "${
            isInaccessible(computedFromEntity) ? '(No access)' : computedFromEntity.name
          }"`,
        icon: 'format-link',
        className: iconClassNames,
      }
    : {
        renderContent: () => (
          <span>
            <p>Inheritance of expiration date overridden.</p>
            {!isDisabled && (
              <a onClick={clearExpirationOverride}>
                <BenchlingIcon icon="format-link" /> Re-sync with entity
              </a>
            )}
          </span>
        ),
        // TODO(BNCH-92651): This should be the new crossed out link icon
        icon: 'format-link',
        className: iconClassNames,
      };
}

export default function ContainerExpirationDate({
  expirationInfo,
  onUpdateExpirationOverride,
  isDisabled,
  confirmBeforeStartEditing,
  disableEditsWithTooltip,
}: ContainerExpirationDateProps): JSX.Element | null {
  const editableDateFieldOnChange = useCallback(
    (datetime: string | null) => {
      const {doUpdate, expirationDateOverride} = validateAndConvertExpirationDatetime(
        datetime,
        expirationInfo.expirationDate
      );
      if (doUpdate) {
        onUpdateExpirationOverride(expirationDateOverride);
      }
    },
    [onUpdateExpirationOverride, expirationInfo.expirationDate]
  );

  if (!BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE || !expirationInfo.isTrackingEnabled) {
    return null;
  }

  return (
    <SectionWrapper elementName="expirationDate">
      <Title>
        Expiration date
        <HelpButton
          renderContent={() => (
            <span>
              When expiration date tracking is enabled, containers inherit expiration dates from their
              contents. Updating a container's expiration date will override this inheritance.
              {/* TODO(BNCH-100259): Add a link to the help article (to be written & tracked by this JIRA issue) */}
            </span>
          )}
          iconClassName="u-mini-spacing-left"
        />
      </Title>
      <div className="u-justify-content-start">
        <EditableDateField
          pickerType="datetime"
          value={expirationInfo.expirationDate}
          onChange={editableDateFieldOnChange}
          viewerPopoverIconProps={getInheritanceIconProps({
            computedFromEntity: expirationInfo.computedFromEntity,
            expirationIsOverridden: expirationInfo.isOverridden,
            isDisabled: isDisabled || disableEditsWithTooltip != null,
            clearExpirationOverride: () => {
              onUpdateExpirationOverride(null);
            },
          })}
          placeholder="Not specified"
          isDisabled={isDisabled}
          confirmBeforeStartEditing={confirmBeforeStartEditing}
          disableEditsWithTooltip={disableEditsWithTooltip}
          shouldShowWarningIcon={expirationInfo.isExpired}
          validFormats={EXPIRATION_DATETIME_FORMATS}
          editPresentationalFormat={EXPIRATION_DATETIME_PRESENTATIONAL_FORMAT}
        />
      </div>
    </SectionWrapper>
  );
}

const styles = StyleSheet.create({
  inheritanceIcon: {
    color: Color.GRAY_500,
  },
});
